import BundleRecommendationDao, {
    BundleRecommendationRunDao,
    BundleRecommendationScopeDao,
} from '@/views/inventory/bundle-recommendations/interfaces/bundle/BundleRecommendation.dao'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'

export interface ImagePlaceholderRequest {
    prompt?: string
    width?: number
    height?: number
}

export interface ImagePlaceholderResponse {
    url: string
    source: 'placeholder' | 'ai'
}

export type BundleContentBundleType = 'multipack' | 'combination' | 'lucky'
export type BundleContentRecommendationStrength = 'strong' | 'moderate' | 'exploratory'
export type BundleContentConfidence = 'high' | 'medium' | 'low'

export interface BundleContentComponent {
    asin: string
    quantityInBundle: number
    title: string
    brand?: string
    modelNumber?: string
    color?: string
    bullets: string[]
    description?: string
}

export interface GenerateBundleContentRequest {
    bundleType: BundleContentBundleType
    marketplace: string
    clientBrand: string
    componentCount: number
    recommendationStrength: BundleContentRecommendationStrength
    components: BundleContentComponent[]
}

export interface GenerateBundleContentResponse {
    bundle_title: string
    bullets: string[]
    description: string
    generation_metadata: {
        confidence: BundleContentConfidence
        warnings: string[]
        source_grounded: boolean
    }
}

export type BundleImageSize = '1024x1024' | '1024x1536' | '1536x1024'
export type BundleImageQuality = 'low' | 'medium' | 'high'

export interface GenerateBundleImageComponent {
    sku: string
    title: string
    quantityInBundle: number
    imageUrl?: string
}

export interface GenerateBundleImageRequest {
    bundleType: BundleContentBundleType
    brand: string
    componentCount: number
    components: GenerateBundleImageComponent[]
    bulkProductTitle?: string
    bulkQuantity?: number
    size?: BundleImageSize
    quality?: BundleImageQuality
}

export default class BundleRecommendationsService extends ApiService {
    async getBundleRecommendations(
        params?: IRequestParameters,
    ): Promise<[BundleRecommendationDao[], PageNumberPagination] | null> {
        return this.request<undefined, [BundleRecommendationDao[], PageNumberPagination]>(
            'GET',
            '/bundle-recommendations',
            params,
            undefined,
            undefined,
            'getBundleRecommendations',
        ).catch(() => null)
    }

    async findDistinctClientIds(): Promise<number[] | null> {
        return this.request<undefined, number[]>(
            'GET',
            '/bundle-recommendations/client-ids',
            undefined,
            undefined,
            undefined,
            'findDistinctClientIds',
        ).catch(() => null)
    }

    async getBundle(id: number): Promise<BundleRecommendationDao | null> {
        return this.request<undefined, BundleRecommendationDao>(
            'GET',
            `/bundle-recommendations/${id}`,
            undefined,
            undefined,
            undefined,
            'getBundleRecommendation',
        ).catch(() => null)
    }

    async findScopes(Client_Id: number): Promise<BundleRecommendationScopeDao[] | null> {
        return this.request<undefined, BundleRecommendationScopeDao[]>(
            'GET',
            '/bundle-recommendations/scopes',
            { Client_Id },
            undefined,
            undefined,
            'findBundleRecommendationScopes',
        ).catch(() => null)
    }

    async getLatestRun(
        Client_Id: number,
        SellerId: string,
        MarketplaceId: string,
    ): Promise<BundleRecommendationRunDao | null> {
        return this.request<undefined, BundleRecommendationRunDao>(
            'GET',
            '/bundle-recommendations/runs/latest',
            { Client_Id, SellerId, MarketplaceId },
            undefined,
            undefined,
            'getLatestBundleRecommendationRun',
        ).catch(() => null)
    }

    async getImagePlaceholder(payload: ImagePlaceholderRequest): Promise<ImagePlaceholderResponse | null> {
        return this.request<ImagePlaceholderRequest, ImagePlaceholderResponse>(
            'POST',
            '/bundle-tool/image-placeholder',
            undefined,
            payload,
            undefined,
            'getImagePlaceholder',
        ).catch(() => null)
    }

    async generateBundleContent(payload: GenerateBundleContentRequest): Promise<GenerateBundleContentResponse | null> {
        return this.request<GenerateBundleContentRequest, GenerateBundleContentResponse>(
            'POST',
            '/bundle-tool/generate-bundle-content',
            undefined,
            payload,
            undefined,
            'generateBundleContent',
        ).catch(() => null)
    }

    async generateAiImage(payload: GenerateBundleImageRequest): Promise<ImagePlaceholderResponse | null> {
        return this.request<GenerateBundleImageRequest, ImagePlaceholderResponse>(
            'POST',
            '/bundle-tool/generate-image',
            undefined,
            payload,
            undefined,
            'generateAiImage',
        ).catch(() => null)
    }

    async regenerateAiImage(payload: GenerateBundleImageRequest): Promise<ImagePlaceholderResponse | null> {
        return this.request<GenerateBundleImageRequest, ImagePlaceholderResponse>(
            'POST',
            '/bundle-tool/regenerate-image',
            undefined,
            payload,
            undefined,
            'regenerateAiImage',
        ).catch(() => null)
    }
}
